import { createSlice } from '@reduxjs/toolkit';
import { performGlobalLogout } from './authSlice';


const MAX_PER_CONV = 100;
const STORAGE_KEY_PREFIX  = 'chat_messages_v3_';

function getStorageKey(userId) {
    return userId ? `${STORAGE_KEY_PREFIX}${userId}` : null;
}

function loadPersistedMessages(userId) {
    const key = getStorageKey(userId);
    if (!key) return {};
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
}

function persistMessages(userId, messages) {
    const key = getStorageKey(userId);
    if (!key) return;
    try {
        const toSave = {};
        Object.entries(messages).forEach(([k, v]) => {
            const real = v.filter(m => m.id && !m.optimistic && typeof m.id === 'number');
            if (real.length > 0) toSave[k] = real.slice(-MAX_PER_CONV);
        });
        sessionStorage.setItem(key, JSON.stringify(toSave));
    } catch (e) {
        console.warn('[ChatSlice] persist failed:', e.message);
    }
}

function clearPersistedMessages(userId) {
    const key = getStorageKey(userId);
    if (!key) return;
    try {
        sessionStorage.removeItem(key);
    } catch (e) {
        console.warn('[ChatSlice] clear failed:', e.message);
    }
}

function now() {
  return new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function buildInitials(name) {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (name || '??').substring(0, 2).toUpperCase();
}

// ═══ INITIAL STATE ═══

const initialState = {
  messages:      {},
  conversations: [],
  activeConvId:  null,
  realConvId:    null,
  typingConvIds: [],
  userId:        null,
};

// ═══ SLICE ═══

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setUserId(state, { payload }) {
      if (state.userId !== payload) {
        state.userId = payload;
        if (payload) {
          state.messages = { ...loadPersistedMessages(payload) };
        } else {
          state.messages = {};
        }
      }
    },
    setActiveConvId(state, { payload }) {
      state.activeConvId = payload;
    },

    addIncomingMessage(state, { payload: { incoming, myUserId } }) {
      const backendConvId = Number(incoming.conversationId);
      let frontendConvId  = null;

      // Step 1: realConvId match
      if (Number(state.realConvId) === backendConvId)
        frontendConvId = state.activeConvId;

      // Step 2: stored realConvId match
      if (!frontendConvId) {
        const convMatch = state.conversations.find(
          c => Number(c.realConvId) === backendConvId
        );
        if (convMatch) frontendConvId = convMatch.id;
      }

      // Step 3: direct id match
      if (!frontendConvId) {
        const directMatch = state.conversations.find(
          c => Number(c.id) === backendConvId
        );
        if (directMatch) frontendConvId = directMatch.id;
      }

      // Step 4: sender/receiver match
      if (!frontendConvId) {
        const userMatch = state.conversations.find(c =>
          Number(c.masterId) === Number(incoming.senderId)   ||
          Number(c.userId)   === Number(incoming.senderId)   ||
          Number(c.masterId) === Number(incoming.receiverId) ||
          Number(c.userId)   === Number(incoming.receiverId)
        );
        if (userMatch) frontendConvId = userMatch.id;
      }

      // Step 5: fallback
      if (!frontendConvId) frontendConvId = state.activeConvId;
      if (!frontendConvId) frontendConvId = backendConvId;
      if (!frontendConvId) return;

      if (!state.messages[frontendConvId]) state.messages[frontendConvId] = [];

      const isMine = Number(incoming.senderId) === Number(myUserId);

      // ✅ optimistic remove on own send
      if (isMine) {
        state.messages[frontendConvId] = state.messages[frontendConvId]
          .filter(m => !m.optimistic);
      }

      if (state.messages[frontendConvId].some(m => m.id === incoming.id)) return;

      state.messages[frontendConvId].push({
        id:       incoming.id,
        sender:   isMine ? 'mine' : 'other',
        senderId: Number(incoming.senderId),
        name:     incoming.name     || 'Unknown',
        initials: incoming.initials || buildInitials(incoming.name || '??'),
        role:     incoming.role     || 'user',
        type:     incoming.type     || 'text',
        text:     incoming.text     || '',
        fileName: incoming.fileName || null,
        fileSize: incoming.fileSize || null,
        mediaUrl: incoming.mediaUrl || null,
        time:     incoming.time     || now(),
        read:     false,
      });

      if (state.messages[frontendConvId].length > MAX_PER_CONV) {
        state.messages[frontendConvId] =
          state.messages[frontendConvId].slice(-MAX_PER_CONV);
      }

      persistMessages(state.userId, state.messages);

  
      const conv = state.conversations.find(c => c.id === frontendConvId);
      if (conv) {
        conv.preview = incoming.text || (incoming.type === 'file' ? '📎 File' : '');
        conv.time    = 'now';
        if (Number(frontendConvId) !== Number(state.activeConvId) && !isMine)
          conv.unread = (conv.unread || 0) + 1;
      }
    },

    setTypingForConv(state, { payload: { convId, userId, myUserId, isTyping } }) {
      if (Number(userId) === Number(myUserId)) return;
      if (isTyping) {
        if (!state.typingConvIds.includes(convId)) state.typingConvIds.push(convId);
      } else {
        state.typingConvIds = state.typingConvIds.filter(id => id !== convId);
      }
    },

  },
  extraReducers: (builder) => {
    builder
      // GLOBAL LOGOUT
      .addCase(performGlobalLogout.fulfilled, (state) => {
        if (state.userId) {
          clearPersistedMessages(state.userId);
        }
        state.messages = {};
        state.conversations = [];
        state.activeConvId = null;
        state.realConvId = null;
        state.typingConvIds = [];
        state.userId = null;
      });
  }
});



export const { addIncomingMessage, setTypingForConv, setActiveConvId, setUserId  } = chatSlice.actions;

export default chatSlice.reducer;